import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { Fragment, cloneElement, isValidElement, createRef, Component, createElement as _createElement } from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import memoize from 'memoize-one';
import Measure from 'react-measure';
import { CLS_PREFIX } from '../../constants';
import Checkbox from '../Checkbox';
import Icon from '../Icon';
import { safeInvoke, isClickable, scrollIntoView, wrapFunction } from '../utils';
import Button from '../Button';
import Translate from '../Translate';
import Text from '../Text';
import { ITEMS_PER_PAGE_OPTIONS } from '../Pagination';
import Spinner from '../Spinner';
import ListOperationContext from './ListOperationContext';
import VerticalContext, { VERTICAL_BREAKPOINT } from '../VerticalContext';
import ListEmptyView from './ListEmptyView';
import Columns, { Column } from '../Columns';
import { FocusVisibleManager, useFocusVisible } from 'use-focus-visible';
import Menu, { MenuItem } from '../Menu';
import Label from '../Label';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// eslint-disable-next-line func-style
function getRowKey(row, props) {
  const {
    rowKey
  } = props;
  if (typeof rowKey === 'function') {
    return rowKey(row);
  }
  return row[rowKey];
}
const Tbody = _ref => {
  let {
    baseClassName = `${CLS_PREFIX}list__wrapper`,
    className,
    ...props
  } = _ref;
  const {
    focusVisible,
    onFocus,
    onBlur
  } = useFocusVisible();
  return /*#__PURE__*/_jsx("tbody", {
    ...props,
    onFocus: wrapFunction(props.onFocus, onFocus),
    onBlur: wrapFunction(props.onBlur, onBlur),
    className: classNames(className, {
      [`${baseClassName}--focus-visible`]: focusVisible
    })
  });
};
const getHumanTotalRows = _ref2 => {
  let {
    selectedRows,
    totalRows,
    allRowsSelected,
    translators
  } = _ref2;
  let mode;
  if (!allRowsSelected && selectedRows === 0) {
    mode = 'totalRows';
  } else if (allRowsSelected || selectedRows === totalRows) {
    mode = 'selectedAllRows';
  } else {
    mode = 'selectedRows';
  }
  const wrapToText = element => /*#__PURE__*/_jsx(Text, {
    fontSize: "sm",
    children: element
  });
  switch (mode) {
    case 'totalRows':
      return wrapToText(/*#__PURE__*/_jsx(Translate, {
        namespace: "List",
        content: "totalRows",
        fallback: "%%totalRows%% items total",
        params: {
          totalRows
        },
        translators: translators
      }));
    case 'selectedRows':
      return wrapToText(/*#__PURE__*/_jsx(Translate, {
        namespace: "List",
        content: "selectedRows",
        fallback: "%%selectedRows%% of %%totalRows%% items selected",
        params: {
          selectedRows: /*#__PURE__*/_jsx(Text, {
            bold: true,
            children: selectedRows
          }),
          totalRows
        },
        translators: translators
      }));
    case 'selectedAllRows':
      return wrapToText(/*#__PURE__*/_jsx(Translate, {
        namespace: "List",
        content: "selectedRows",
        fallback: "%%selectedRows%% of %%totalRows%% items selected",
        params: {
          selectedRows: /*#__PURE__*/_jsx(Text, {
            bold: true,
            children: /*#__PURE__*/_jsx(Translate, {
              namespace: "List",
              content: "all",
              fallback: "All",
              translators: translators
            })
          }),
          totalRows
        },
        translators: translators
      }));
    default:
      return null;
  }
};
/**
 * `List` component is used for displaying a collection of tabular data.
 * [More details about designing of lists.](#!/Sets%20of%20Objects)
 * @since 0.0.42
 */
class List extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "state", {
      selectAllActive: false,
      sortInPlace: !this.props.pagination,
      vertical: false,
      expandedRows: this.props.expandedRows ?? [],
      ReorderableList: undefined,
      reorderableItemWidths: [],
      prevExpandedRows: this.props.expandedRows ?? [],
      sortColumn: undefined,
      prevSortColumn: undefined,
      sortDirection: undefined,
      prevSortDirection: undefined,
      selection: this.props.selection ?? [],
      prevSelection: this.props.selection ?? [],
      prevData: [],
      prevPagination: undefined
    });
    _defineProperty(this, "needScroll", false);
    _defineProperty(this, "anchorRef", /*#__PURE__*/createRef());
    _defineProperty(this, "flatData", data => data.reduce((acc, item) => {
      const {
        data
      } = item;
      const {
        virtual
      } = this.getRowProps(item);
      if (virtual) {
        return [...acc, ...(data ? this.flatData(data) : [])];
      }
      return [...acc, item, ...(data ? this.flatData(data) : [])];
    }, []));
    _defineProperty(this, "handleRowSelectionChange", (checked, rowKey) => {
      const {
        onAllRowsSelectedChange
      } = this.props;
      let selection;
      if (checked) {
        selection = this.state.selection.slice();
        selection.push(rowKey);
      } else {
        selection = this.state.selection.filter(value => value !== rowKey);
        safeInvoke(onAllRowsSelectedChange, false);
      }
      this.setSelection(selection);
      this.toggleSelectionMenu(false);
    });
    _defineProperty(this, "toggleSelectionMenu", nextState => {
      this.setState(_ref3 => {
        let {
          selectAllActive
        } = _ref3;
        return {
          selectAllActive: nextState ?? !selectAllActive
        };
      });
    });
    _defineProperty(this, "handleSelectAll", () => {
      this.handleGlobalSelectionChange(true);
      safeInvoke(this.props.onAllRowsSelectedChange, true);
    });
    _defineProperty(this, "handleDeselectAll", () => {
      this.handleGlobalSelectionChange(false);
      safeInvoke(this.props.onAllRowsSelectedChange, false);
    });
    _defineProperty(this, "handleGlobalSelectionChange", headerChecked => {
      const {
        onAllRowsSelectedChange,
        data
      } = this.props;
      const {
        selection
      } = this.state;
      const nextSelection = [];
      this.flatData(data ?? []).forEach(row => {
        const key = getRowKey(row, this.props);
        if (row.disabled || this.isRowLoading(row)) {
          if (selection.indexOf(key) !== -1) {
            nextSelection.push(key);
          }
          return;
        }
        if (headerChecked) {
          nextSelection.push(key);
        }
      });
      if (!headerChecked) {
        safeInvoke(onAllRowsSelectedChange, false);
      }
      this.setSelection(nextSelection);
    });
    _defineProperty(this, "handlePageSelectionChange", () => {
      const {
        data,
        pagination,
        totalRows
      } = this.props;
      const {
        selection
      } = this.state;
      if (!pagination || !totalRows || !pagination.props.current || !pagination.props.itemsPerPage) {
        return;
      }
      const itemsPerPage = pagination.props.itemsPerPage === 'all' ? totalRows : pagination.props.itemsPerPage;
      const startIndex = (pagination.props.current - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const currentPageData = this.flatData(data ?? []).slice(startIndex, endIndex);
      const nextSelection = [];
      currentPageData.forEach(row => {
        const key = getRowKey(row, this.props);
        if (row.disabled || this.isRowLoading(row)) {
          if (selection.indexOf(key) !== -1) {
            nextSelection.push(key);
          }
          return;
        }
        nextSelection.push(key);
      });
      this.setSelection(nextSelection);
    });
    _defineProperty(this, "handleResize", _ref4 => {
      let {
        bounds
      } = _ref4;
      if (bounds && this.props.vertical) {
        this.setState({
          vertical: typeof this.props.vertical === 'boolean' ? this.props.vertical : bounds.width < this.props.vertical
        });
      }
    });
    _defineProperty(this, "isRowLoading", row => this.props.loadingRows?.indexOf(getRowKey(row, this.props)) !== -1);
    _defineProperty(this, "getRowTitle", row => {
      const {
        columns
      } = this.props;
      const column = columns.find(c => c.type === 'title');
      if (column && column.key) {
        return row[column.key];
      }
      return undefined;
    });
    _defineProperty(this, "getRowProps", row => {
      const {
        rowProps
      } = this.props;
      if (typeof rowProps === 'function') {
        return {
          ...rowProps(row)
        };
      }
      return {
        ...rowProps
      };
    });
    _defineProperty(this, "isAllRowsChecked", memoize((rows, selectedKeys) => {
      const filteredRows = this.flatData(rows).filter(row => !row.disabled);
      if (filteredRows.length > 0) {
        return filteredRows.every(row => {
          const key = getRowKey(row, this.props);
          return selectedKeys.indexOf(key) !== -1;
        });
      }
      return false;
    }));
    _defineProperty(this, "isAllRowsCheckedOnPage", memoize((rows, selectedKeys) => {
      const {
        pagination,
        totalRows
      } = this.props;
      if (!pagination || !totalRows || !pagination.props.current || !pagination.props.itemsPerPage) {
        return false;
      }
      const {
        current
      } = pagination.props;
      const itemsPerPage = pagination.props.itemsPerPage === 'all' ? totalRows : pagination.props.itemsPerPage;
      const startIndex = (current - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const currentPageData = this.flatData(rows ?? []).slice(startIndex, endIndex).filter(row => !row.disabled);
      if (currentPageData.length > 0) {
        return currentPageData.every(row => {
          const key = getRowKey(row, this.props);
          return selectedKeys.includes(key);
        });
      }
      return false;
    }));
    _defineProperty(this, "isAnyRowsExpanded", memoize((rows, expandedRows) => this.flatData(rows).some(row => {
      const key = getRowKey(row, this.props);
      return expandedRows.indexOf(key) !== -1;
    })));
    _defineProperty(this, "handleGlobalExpandingChange", () => {
      const {
        data
      } = this.props;
      const {
        expandedRows
      } = this.state;
      const headerExpanded = this.isAnyRowsExpanded(data, expandedRows);
      const nextExpandedRows = [];
      this.flatData(data ?? []).forEach(row => {
        const key = getRowKey(row, this.props);
        if (!this.isRowExpandable(row)) {
          if (expandedRows.indexOf(key) !== -1) {
            nextExpandedRows.push(key);
          }
          return;
        }
        if (!headerExpanded) {
          nextExpandedRows.push(key);
        }
      });
      this.setExpandedRows(nextExpandedRows);
    });
    _defineProperty(this, "createRowClickHandler", row => e => {
      if (isClickable(e)) {
        return;
      }
      if (this.isRowExpandable(row)) {
        this.handleRowExpandingChange(row);
      }
    });
  }
  static getDerivedStateFromProps(_ref5, _ref6) {
    let {
      sortColumn,
      sortDirection,
      selection,
      expandedRows,
      notExpandableRows,
      renderRowBody,
      data,
      pagination,
      vertical,
      rowKey,
      totalRows,
      filtered,
      autoExpandSingleItem
    } = _ref5;
    let {
      prevSortColumn,
      prevSortDirection,
      prevData,
      prevSelection,
      prevPagination,
      prevExpandedRows,
      ...state
    } = _ref6;
    let nextState = {};
    if (prevSortColumn !== sortColumn || prevSortDirection !== sortDirection) {
      nextState = {
        ...nextState,
        sortColumn,
        sortDirection
      };
    }
    const additionalExpanded = [];
    if (prevData !== data) {
      const isSingleElementInCurrentExpanded = () => data && state.expandedRows.includes(getRowKey(data[0], {
        rowKey
      }));
      if (renderRowBody && data?.length === 1 && (!pagination || totalRows === 1) && (!prevData || prevData?.length === 0 || isSingleElementInCurrentExpanded()) && (!filtered || isSingleElementInCurrentExpanded()) && autoExpandSingleItem) {
        const key = getRowKey(data[0], {
          rowKey
        });
        if (notExpandableRows?.indexOf(key) === -1) {
          additionalExpanded.push(key);
        }
      }
      const keys = (data ?? []).map(row => getRowKey(row, {
        rowKey
      }));
      const nextSelection = state.selection.filter(key => keys.indexOf(key) !== -1);
      nextState = {
        ...nextState,
        selection: nextSelection
      };
    }
    if (prevSelection !== selection) {
      nextState = {
        ...nextState,
        selection: selection ?? []
      };
    }
    if (prevExpandedRows !== expandedRows || additionalExpanded.length) {
      nextState = {
        ...nextState,
        expandedRows: [...(expandedRows ?? []), ...additionalExpanded]
      };
    }
    if (prevPagination !== pagination) {
      nextState = {
        ...nextState,
        sortInPlace: !pagination
      };
    }
    if (typeof vertical === 'boolean') {
      nextState.vertical = vertical;
    }
    nextState.prevSortColumn = sortColumn;
    nextState.prevSortDirection = sortDirection;
    nextState.prevData = data;
    nextState.prevSelection = selection;
    nextState.prevPagination = pagination;
    nextState.prevExpandedRows = expandedRows;
    return nextState;
  }
  componentDidMount() {
    if (this.props.reorderable) {
      this.applyReorderableList();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      data
    } = this.props;
    const {
      data: prevData
    } = prevProps;
    if (data !== prevData && this.needScroll && this.anchorRef.current) {
      scrollIntoView(this.anchorRef.current, {
        scrollMode: 'if-needed'
      });
      this.needScroll = false;
    }
    if (prevProps.reorderable !== this.props.reorderable && this.props.reorderable) {
      this.applyReorderableList();
    }
  }
  applyReorderableList() {
    import(/* webpackChunkName: "react-movable" */'@plesk/react-movable').then(_ref7 => {
      let {
        List: ReorderableList
      } = _ref7;
      // @ts-expect-error
      this.setState({
        ReorderableList
      });
    });
  }
  getData() {
    const {
      sortInPlace,
      sortColumn,
      sortDirection
    } = this.state;
    const {
      data,
      columns
    } = this.props;
    if (!data) {
      return [];
    }
    if (!sortInPlace) {
      return data;
    }
    const [column] = columns.filter(_ref8 => {
      let {
        key
      } = _ref8;
      return key === sortColumn;
    });
    const defaultSort = (a, b) => {
      if (a > b) {
        return 1;
      }
      if (a < b) {
        return -1;
      }
      return 0;
    };
    if (!column || !sortColumn) {
      return data;
    }
    return data.sort((a, b) => (column.sort ? column.sort(a, b) : defaultSort(a[sortColumn], b[sortColumn])) * (sortDirection === 'ASC' ? 1 : -1));
  }
  sort(columnKey) {
    const {
      sortInPlace
    } = this.state;
    const {
      sortColumn,
      sortDirection
    } = this.state;
    const nextSortDirection = sortColumn === columnKey && sortDirection === 'ASC' ? 'DESC' : 'ASC';
    const nextSortColumn = columnKey;
    if (sortInPlace) {
      this.setState({
        sortColumn: nextSortColumn,
        sortDirection: nextSortDirection
      });
    }
    if (this.props.onSortChange) {
      this.props.onSortChange({
        sortColumn: nextSortColumn,
        sortDirection: nextSortDirection
      });
    }
  }
  isRowSelected(row) {
    return this.state.selection.indexOf(getRowKey(row, this.props)) !== -1;
  }
  setSelection(selection) {
    this.setState({
      selection
    });
    if (this.props.onSelectionChange) {
      this.props.onSelectionChange(selection.slice());
    }
  }
  isRowExpanded(row) {
    return this.state.expandedRows.indexOf(getRowKey(row, this.props)) !== -1;
  }
  isRowExpandable(row) {
    const {
      virtual
    } = this.getRowProps(row);
    if (virtual) {
      return false;
    }
    return this.props.notExpandableRows?.indexOf(getRowKey(row, this.props)) === -1;
  }
  handleRowExpandingChange(row) {
    let expandedRows;
    const rowKey = getRowKey(row, this.props);
    if (this.isRowExpanded(row)) {
      expandedRows = this.state.expandedRows.filter(value => value !== rowKey);
    } else {
      expandedRows = this.state.expandedRows.slice();
      expandedRows.push(rowKey);
    }
    this.setExpandedRows(expandedRows);
  }
  setExpandedRows(expandedRows) {
    this.setState({
      expandedRows
    });
    safeInvoke(this.props.onExpandedRowsChange, expandedRows.slice());
  }
  renderRow(_ref9) {
    let {
      row,
      index,
      hasTitle,
      realColumn,
      parent,
      isDragged,
      reorderableProps
    } = _ref9;
    const {
      baseClassName,
      renderRowBody,
      reorderable
    } = this.props;
    const {
      vertical,
      reorderableItemWidths
    } = this.state;
    const hasActions = realColumn.some(_ref10 => {
      let {
        type
      } = _ref10;
      return type === 'actions';
    });
    const {
      className,
      virtual,
      ...rowProps
    } = this.getRowProps(row);
    let skipCells = 0;
    const Wrapper = reorderable && !parent ? Tbody : Fragment;
    const element = /*#__PURE__*/_createElement(Wrapper, {
      ...(Wrapper === Tbody && !isDragged ? reorderableProps : undefined),
      key: getRowKey(row, this.props)
    }, /*#__PURE__*/_jsx("tr", {
      className: classNames({
        'is-selected': this.isRowSelected(row),
        [`${baseClassName}__row--expanded`]: this.isRowExpanded(row),
        [`${baseClassName}__row--expandable`]: renderRowBody && this.isRowExpandable(row),
        [`${baseClassName}__row--parent`]: row.data,
        [`${baseClassName}__row--virtual`]: virtual,
        [`${baseClassName}__row--child`]: parent,
        [`${baseClassName}__row--reorderable`]: reorderable,
        [`${baseClassName}__row--${row.intent}`]: row.intent
      }, className),
      ...rowProps,
      onClick: renderRowBody ? this.createRowClickHandler(row) : undefined,
      children: realColumn.map((_ref11, index) => {
        let {
          key,
          title,
          render,
          width,
          cellProps,
          truncate,
          type
        } = _ref11;
        if (skipCells > 0) {
          skipCells -= 1;
          return null;
        }
        let style;
        if (!hasTitle && width && !vertical) {
          style = {
            width
          };
        }
        if (isDragged && reorderableItemWidths) {
          style = {
            width: reorderableItemWidths[index]
          };
        }
        let content = render ? render(row, {
          parent
        }) : row[key];
        // cell props from render
        if (content && content.children && ! /*#__PURE__*/isValidElement(content)) {
          const {
            children,
            ...other
          } = content;
          content = children;
          cellProps = {
            ...cellProps,
            ...other
          };
          if (other.colSpan) {
            skipCells += other.colSpan - 1;
          }
        }
        const isCellEmpty = !title && !content;
        if (truncate) {
          content = /*#__PURE__*/_jsx("div", {
            className: `${baseClassName}__truncate`,
            children: /*#__PURE__*/_jsx(Text, {
              truncate: true,
              children: content
            })
          });
        }
        if (vertical) {
          const hasColumnTitle = type !== 'internal' && type !== 'actions' && type !== 'title' && title;
          if (!isCellEmpty && type !== 'internal') {
            content = /*#__PURE__*/_jsxs(_Fragment, {
              children: [hasColumnTitle && /*#__PURE__*/_jsx("div", {
                className: `${baseClassName}__cell-inner-title`,
                children: title
              }), /*#__PURE__*/_jsx("div", {
                className: `${baseClassName}__cell-inner-content`,
                children: content
              })]
            });
          }
          if (type === 'actions') {
            content = /*#__PURE__*/_jsx(VerticalContext.Provider, {
              value: true,
              children: content
            });
          }
        }

        // do not render reorderable handler for nested rows
        if (key === 'reorderable' && type === 'internal' && parent) {
          content = null;
        }
        return /*#__PURE__*/_jsx("td", {
          style: style,
          ...cellProps,
          className: classNames(cellProps && cellProps.className, {
            [`${baseClassName}__cell-controls`]: type === 'controls',
            [`${baseClassName}__cell-actions`]: type === 'actions',
            [`${baseClassName}__cell-title`]: type === 'title',
            [`${baseClassName}__cell-has-actions`]: type === 'title' && hasActions
          }) || undefined,
          children: content
        }, key);
      })
    }), renderRowBody && !virtual && this.renderRowBody({
      row,
      index,
      realColumn,
      parent
    }), row.data ? row.data.map((childRow, index) => this.renderRow({
      row: childRow,
      index,
      hasTitle,
      realColumn,
      parent: row
    })) : null);
    if (isDragged) {
      return /*#__PURE__*/_jsx("div", {
        className: classNames(baseClassName, `${baseClassName}--dragged`),
        ...reorderableProps,
        children: /*#__PURE__*/_jsx("table", {
          className: classNames(`${baseClassName}__table`, `${baseClassName}__table--reorderable`, {
            [`${baseClassName}--expandable`]: renderRowBody,
            [`${baseClassName}__table--vertical`]: vertical
          }),
          children: element
        })
      });
    }
    return element;
  }
  renderRowBody(_ref12) {
    let {
      row,
      index,
      realColumn,
      parent
    } = _ref12;
    const {
      baseClassName,
      renderRowBody
    } = this.props;
    const {
      vertical
    } = this.state;
    if (!renderRowBody) {
      return null;
    }
    const reorderable = realColumn.find(_ref13 => {
      let {
        key
      } = _ref13;
      return key === 'reorderable';
    });
    const expander = realColumn.find(_ref14 => {
      let {
        key
      } = _ref14;
      return key === 'expander';
    });
    const isRowExpanded = this.isRowExpanded(row);
    const trRef = /*#__PURE__*/createRef();
    return /*#__PURE__*/_jsx(CSSTransition, {
      in: isRowExpanded,
      unmountOnExit: true,
      timeout: {
        enter: 0,
        exit: 300
      },
      classNames: `${baseClassName}__row-body-`,
      nodeRef: trRef,
      children: state => /*#__PURE__*/_jsxs("tr", {
        className: `${baseClassName}__row-body`,
        ref: trRef,
        children: [reorderable ? /*#__PURE__*/_jsx("td", {
          className: `${baseClassName}__cell-reorderable`
        }) : null, expander ? /*#__PURE__*/_jsx("td", {
          className: `${baseClassName}__cell-expander`
        }) : null, /*#__PURE__*/_jsx("td", {
          colSpan: realColumn.length - (reorderable ? 1 : 0) - (expander ? 1 : 0),
          className: `${baseClassName}__cell-body`,
          children: /*#__PURE__*/_jsx("div", {
            className: `${baseClassName}__row-body-content-wrapper`,
            children: /*#__PURE__*/_jsx("div", {
              className: `${baseClassName}__row-body-content ${baseClassName}__row-body-content--${state}`,
              children: /*#__PURE__*/_jsx(VerticalContext.Provider, {
                value: vertical,
                children: renderRowBody(row, {
                  index,
                  parent
                })
              })
            })
          })
        })]
      })
    });
  }
  renderToolbar() {
    const {
      toolbar,
      allRowsSelected,
      totalRows,
      data,
      locale
    } = this.props;
    const {
      selection
    } = this.state;
    let selectedRowTitle;
    if (selection.length === 1) {
      const row = this.flatData(data).find(row => getRowKey(row, this.props) === selection[0]);
      if (row) {
        selectedRowTitle = this.getRowTitle(row);
      }
    }
    if (!toolbar) {
      return null;
    }
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const context = {
      selectedRows: allRowsSelected ? totalRows || /*#__PURE__*/_jsx(Translate, {
        namespace: "List",
        content: "all",
        fallback: "all",
        translators: locale
      }) : selection.length,
      selectedRowTitle
    };
    return /*#__PURE__*/_jsx(ListOperationContext.Provider, {
      value: context,
      children: toolbar
    });
  }
  shouldBePagination() {
    const {
      totalRows,
      pagination
    } = this.props;
    if (!pagination || !totalRows) {
      return false;
    }
    if (totalRows && /*#__PURE__*/isValidElement(pagination)) {
      const {
        itemsPerPageOptions = ITEMS_PER_PAGE_OPTIONS
      } = pagination.props;
      if (Array.isArray(itemsPerPageOptions)) {
        const numericOptions = itemsPerPageOptions.filter(Number);
        if (numericOptions.length) {
          const min = Math.min(...numericOptions);
          if (totalRows <= min) {
            return false;
          }
        }
      }
    }
    return true;
  }
  renderHeader() {
    const {
      baseClassName,
      allRowsSelected,
      totalRows,
      locale
    } = this.props;
    const {
      selection
    } = this.state;
    const shouldBePagination = this.shouldBePagination();
    if (totalRows === undefined && !shouldBePagination) {
      return null;
    }
    return /*#__PURE__*/_jsxs("div", {
      className: `${baseClassName}__header`,
      children: [totalRows === undefined ? /*#__PURE__*/_jsx("span", {}) : /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx("span", {
          className: `${baseClassName}__header-text`,
          children: getHumanTotalRows({
            selectedRows: selection.length,
            totalRows,
            allRowsSelected,
            translators: locale
          })
        })
      }), shouldBePagination && /*#__PURE__*/_jsx("div", {
        className: `${baseClassName}__header-text`,
        children: this.renderPagination('header')
      })]
    });
  }
  renderEmptyView() {
    const {
      emptyView,
      filtered
    } = this.props;
    if (emptyView === undefined) {
      return filtered ? /*#__PURE__*/_jsx(ListEmptyView, {
        reason: "filtered"
      }) : /*#__PURE__*/_jsx(ListEmptyView, {});
    }
    return emptyView;
  }
  renderPagination(placement) {
    const {
      pagination,
      loading
    } = this.props;
    if (!pagination) {
      return null;
    }
    const paginationProps = {
      loading
    };
    switch (placement) {
      case 'header':
        paginationProps.view = 'popover';
        break;
      case 'footer':
        paginationProps.onSelect = current => {
          this.needScroll = true;
          safeInvoke(pagination.props.onSelect, current);
        };
        if (pagination.props.onItemsPerPageChange) {
          paginationProps.onItemsPerPageChange = itemsPerPage => {
            this.needScroll = true;
            safeInvoke(pagination.props.onItemsPerPageChange, itemsPerPage);
          };
        }
        break;
    }
    return /*#__PURE__*/cloneElement(pagination, {
      ...paginationProps
    });
  }
  renderSelectionMenu(headerCheckboxProps) {
    const {
      allRowsSelected,
      baseClassName,
      totalRows,
      data,
      locale,
      onSelectAll
    } = this.props;
    const {
      selection
    } = this.state;
    const selectedRows = selection.length;
    const shouldBePagination = this.shouldBePagination();
    const totalRowsCount = totalRows || this.flatData(data ?? []).filter(row => !row.disabled).length;
    const isAllSelected = allRowsSelected || selectedRows === totalRowsCount;
    const isAllOnPageSelected = this.isAllRowsCheckedOnPage(data, selection);
    const itemsPerPage = this.props?.pagination?.props.itemsPerPage;
    return shouldBePagination ? /*#__PURE__*/_jsxs("div", {
      className: `${baseClassName}__select-all-wrapper`,
      children: [/*#__PURE__*/_jsx(Checkbox, {
        ...headerCheckboxProps
      }), this.state.selectAllActive && /*#__PURE__*/_jsxs(Menu, {
        className: `${baseClassName}__select-all-menu`,
        children: [isAllOnPageSelected && /*#__PURE__*/_jsxs(MenuItem, {
          disabled: true,
          children: [/*#__PURE__*/_jsx(Translate, {
            namespace: "List",
            content: "allItemsOnPageSelected",
            fallback: "All items on the page selected",
            translators: locale
          }), /*#__PURE__*/_jsx(Label, {
            intent: "info",
            children: itemsPerPage
          })]
        }), isAllSelected ? /*#__PURE__*/_jsxs(MenuItem, {
          disabled: true,
          children: [/*#__PURE__*/_jsx(Translate, {
            namespace: "List",
            content: "allItemsSelected",
            fallback: "All items selected",
            translators: locale
          }), /*#__PURE__*/_jsx(Label, {
            intent: "info",
            children: totalRowsCount
          })]
        }) : /*#__PURE__*/_jsxs(MenuItem, {
          onClick: () => {
            this.handleSelectAll();
            this.toggleSelectionMenu();
            if (onSelectAll) {
              onSelectAll('SELECT_ALL');
            }
          },
          children: [/*#__PURE__*/_jsx(Translate, {
            namespace: "List",
            content: "selectAllItems",
            fallback: "Select all items",
            translators: locale
          }), /*#__PURE__*/_jsx(Label, {
            intent: "inactive",
            children: totalRowsCount
          })]
        }), !isAllOnPageSelected && /*#__PURE__*/_jsxs(MenuItem, {
          disabled: isAllOnPageSelected,
          onClick: () => {
            this.handlePageSelectionChange();
            this.toggleSelectionMenu();
            if (onSelectAll) {
              onSelectAll('SELECT_ALL_ON_PAGE');
            }
          },
          children: [/*#__PURE__*/_jsx(Translate, {
            namespace: "List",
            content: "selectAllItemsOnPage",
            fallback: "Select all items on the page",
            translators: locale
          }), /*#__PURE__*/_jsx(Label, {
            intent: isAllSelected ? 'info' : 'inactive',
            children: itemsPerPage
          })]
        }), (isAllSelected || isAllOnPageSelected) && /*#__PURE__*/_jsx(MenuItem, {
          onClick: () => {
            this.handleDeselectAll();
            this.toggleSelectionMenu();
          },
          children: /*#__PURE__*/_jsx(Translate, {
            namespace: "List",
            content: "deselectAll",
            fallback: "Deselect",
            translators: locale
          })
        })]
      })]
    }) : /*#__PURE__*/_jsx(Checkbox, {
      ...headerCheckboxProps
    });
  }
  render() {
    const {
      baseClassName,
      className,
      data,
      columns,
      sortColumn,
      sortDirection,
      onSortChange,
      selection,
      onSelectionChange,
      renderSelectionCheckbox,
      rowKey,
      renderRowBody,
      expandedRows,
      notExpandableRows,
      onExpandedRowsChange,
      loadingRows,
      pagination,
      totalRows,
      toolbar,
      allRowsSelected,
      onAllRowsSelectedChange,
      filtered,
      emptyView,
      emptyViewMode,
      sidebar,
      locale,
      vertical: verticalProp,
      loading,
      rowProps,
      reorderable,
      onReorderEnd,
      autoExpandSingleItem,
      ...props
    } = this.props;
    const {
      vertical
    } = this.state;
    if (!(data && data.length > 0) && !filtered && emptyViewMode === 'all') {
      return this.renderEmptyView();
    }
    const realColumn = columns.map((column, i) => {
      const classList = [];
      const cellClassList = [];
      if (i === 0) {
        cellClassList.push(`${baseClassName}__cell-first`);
      }
      if (column.align && !vertical) {
        const className = `${baseClassName}__cell-${column.align}`;
        classList.push(className);
        cellClassList.push(className);
      }
      if (column.className) {
        classList.push(column.className);
        cellClassList.push(column.className);
      }
      const newColumn = {
        ...column
      };
      if (classList.length) {
        newColumn.className = classNames(...classList);
      }
      if (cellClassList.length) {
        newColumn.cellProps = {
          className: classNames(...cellClassList)
        };
      }
      return newColumn;
    });
    if (onSelectionChange) {
      const shouldBePagination = this.shouldBePagination();
      const headerChecked = this.isAllRowsChecked(data, this.state.selection) || this.isAllRowsCheckedOnPage(data, this.state.selection);
      const headerCheckboxProps = {
        checked: headerChecked,
        indeterminate: onAllRowsSelectedChange && shouldBePagination ? this.state.selectAllActive : undefined,
        onChange: onAllRowsSelectedChange && shouldBePagination ? () => this.toggleSelectionMenu() : this.handleGlobalSelectionChange
      };
      let title = /*#__PURE__*/_jsx(Checkbox, {
        ...headerCheckboxProps
      });
      if (onAllRowsSelectedChange) {
        title = this.renderSelectionMenu(headerCheckboxProps);
      } else if (typeof renderSelectionCheckbox === 'function') {
        title = renderSelectionCheckbox(headerCheckboxProps);
      }
      realColumn.unshift({
        key: 'selection',
        type: 'internal',
        className: `${baseClassName}__cell-selection`,
        cellProps: {
          className: `${baseClassName}__cell-selection`
        },
        title,
        render: row => {
          const {
            virtual
          } = this.getRowProps(row);
          if (virtual) {
            return null;
          }
          if (this.isRowLoading(row)) {
            return /*#__PURE__*/_jsx(Spinner, {
              className: `${baseClassName}__spinner`
            });
          }
          const checkboxProps = {
            checked: this.isRowSelected(row),
            disabled: row.disabled,
            onChange: checked => this.handleRowSelectionChange(checked, getRowKey(row, this.props)),
            inputProps: {
              value: getRowKey(row, this.props)
            }
          };
          if (typeof renderSelectionCheckbox === 'function') {
            return renderSelectionCheckbox(checkboxProps, row);
          }
          return /*#__PURE__*/_jsx(Checkbox, {
            ...checkboxProps
          });
        }
      });
    }
    if (renderRowBody) {
      realColumn.unshift({
        key: 'expander',
        type: 'internal',
        className: `${baseClassName}__cell-expander`,
        cellProps: {
          className: `${baseClassName}__cell-expander`
        },
        title: /*#__PURE__*/_jsx(Button, {
          ghost: true,
          className: `${baseClassName}__row-expander`,
          icon: /*#__PURE__*/_jsx(Icon, {
            className: `${baseClassName}__row-expander-icon`,
            children: /*#__PURE__*/_jsx("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 10 10",
              children: /*#__PURE__*/_jsx("path", {
                fill: "none",
                stroke: "currentColor",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                d: "M1.5 1.4L5 4.9l3.5-3.5M1.5 5.1L5 8.6l3.5-3.5"
              })
            })
          }),
          onClick: this.handleGlobalExpandingChange
        }),
        render: row => this.isRowExpandable(row) && /*#__PURE__*/_jsx(Button, {
          ghost: true,
          className: `${baseClassName}__row-expander`,
          icon: /*#__PURE__*/_jsx(Icon, {
            className: `${baseClassName}__row-expander-icon`,
            children: /*#__PURE__*/_jsx("svg", {
              xmlns: "http://www.w3.org/2000/svg",
              viewBox: "0 0 10 10",
              children: /*#__PURE__*/_jsx("path", {
                fill: "none",
                stroke: "currentColor",
                strokeLinecap: "round",
                strokeLinejoin: "round",
                d: "M1.5 3.2L5 6.7l3.5-3.5"
              })
            })
          }),
          onClick: () => this.handleRowExpandingChange(row)
        })
      });
    }
    if (reorderable) {
      realColumn.unshift({
        key: 'reorderable',
        type: 'internal',
        className: `${baseClassName}__cell-reorderable`,
        cellProps: {
          className: `${baseClassName}__cell-reorderable`
        },
        title: null,
        render: () => /*#__PURE__*/_jsx("span", {
          className: `${baseClassName}__cell-reorderable-indicator`,
          "data-movable-handle": true,
          children: /*#__PURE__*/_jsx("svg", {
            width: "6",
            height: "14",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            "aria-hidden": "true",
            children: /*#__PURE__*/_jsx("path", {
              d: "M0 0h2v2H0zM0 4h2v2H0zM0 8h2v2H0zM0 12h2v2H0zM4 0h2v2H4zM4 4h2v2H4zM4 8h2v2H4zM4 12h2v2H4z",
              fill: "currentColor"
            })
          })
        })
      });
    }
    const hasTitle = !vertical && columns.some(column => column.title);
    let content;
    if (!(data && data.length > 0) && (filtered || emptyViewMode === 'items')) {
      content = this.renderEmptyView();
    } else {
      const renderList = _ref15 => {
        let {
          children,
          props: reorderableProps
        } = _ref15;
        return /*#__PURE__*/_jsx(FocusVisibleManager, {
          children: /*#__PURE__*/_jsxs("table", {
            className: classNames(`${baseClassName}__table`, {
              [`${baseClassName}--expandable`]: renderRowBody,
              [`${baseClassName}__table--vertical`]: vertical,
              [`${baseClassName}__table--reorderable`]: reorderable
            }),
            ...reorderableProps,
            children: [hasTitle && /*#__PURE__*/_jsx("thead", {
              children: /*#__PURE__*/_jsx("tr", {
                className: classNames(`${baseClassName}__table-thead`, {
                  [`${baseClassName}__row--expanded`]: this.isAnyRowsExpanded(data, this.state.expandedRows)
                }),
                children: realColumn.map(_ref16 => {
                  let {
                    key,
                    title,
                    sortable,
                    sort,
                    width,
                    render,
                    truncate,
                    type,
                    cellProps,
                    ...headerProps
                  } = _ref16;
                  if (sortable) {
                    title = /*#__PURE__*/_jsx("div", {
                      className: "th__action",
                      children: title
                    });
                    headerProps.className = classNames({
                      'is-sortable': sortable,
                      'is-sorted is-sorted--up': this.state.sortColumn === key && this.state.sortDirection === 'ASC',
                      'is-sorted is-sorted--down': this.state.sortColumn === key && this.state.sortDirection === 'DESC'
                    }, headerProps.className);
                    headerProps.onClick = () => this.sort(key);
                  }
                  if (width) {
                    headerProps.style = {
                      ...headerProps.style,
                      width
                    };
                  }
                  return /*#__PURE__*/_jsx("th", {
                    ...headerProps,
                    children: title
                  }, key);
                })
              })
            }), reorderable ? children : /*#__PURE__*/_jsx("tbody", {
              children: children
            })]
          })
        });
      };
      const renderItem = _ref17 => {
        let {
          value: {
            row
          },
          index,
          props: reorderableProps,
          isDragged
        } = _ref17;
        return this.renderRow({
          row,
          index,
          hasTitle,
          realColumn,
          reorderableProps,
          isDragged
        });
      };
      if (reorderable && this.state.ReorderableList) {
        const {
          ReorderableList
        } = this.state;
        content = /*#__PURE__*/_jsx(ReorderableList, {
          beforeDrag: _ref18 => {
            let {
              elements,
              index
            } = _ref18;
            const cells = Array.from(elements[index].firstElementChild.children);
            const reorderableItemWidths = cells.map(cell => window.getComputedStyle(cell).width);
            this.setState({
              reorderableItemWidths
            });
          },
          onChange: _ref19 => {
            let {
              oldIndex,
              newIndex
            } = _ref19;
            safeInvoke(onReorderEnd, {
              oldIndex,
              newIndex
            });
          },
          values: this.getData().map(row => ({
            row
          })),
          renderList: renderList,
          renderItem: renderItem,
          lockVertically: true,
          hooks: {
            getChildren: children =>
            // @ts-expect-error
            children.filter(node => node.nodeName !== 'THEAD')
          },
          useDragHandle: false
        });
      } else {
        content = renderList({
          children: this.getData().map((row, index) => renderItem({
            value: {
              row
            },
            index
          }))
        });
      }
    }
    content = /*#__PURE__*/_jsx("div", {
      className: classNames(`${baseClassName}__table-container`, {
        [`${baseClassName}__table-container--loading`]: loading
      }),
      children: content
    });
    if (sidebar) {
      content = /*#__PURE__*/_jsxs(Columns, {
        gap: "md",
        className: classNames(`${baseClassName}__content`, {
          [`${baseClassName}__content--vertical`]: vertical
        }),
        children: [/*#__PURE__*/_jsx(Column, {
          fill: true,
          children: content
        }), /*#__PURE__*/_jsx(Column, {
          className: `${baseClassName}__sidebar`,
          children: sidebar
        })]
      });
    }
    const shouldBePagination = this.shouldBePagination();
    const createRoot = ref => /*#__PURE__*/_jsxs("div", {
      ref: ref,
      className: classNames(baseClassName, className),
      ...props,
      children: [this.renderHeader(), this.renderToolbar(), shouldBePagination && /*#__PURE__*/_jsx("span", {
        ref: this.anchorRef
      }), content, shouldBePagination && /*#__PURE__*/_jsx("div", {
        className: `${baseClassName}__footer`,
        children: this.renderPagination('footer')
      })]
    });
    if (typeof verticalProp === 'number') {
      return /*#__PURE__*/_jsx(Measure, {
        bounds: true,
        onResize: this.handleResize,
        children: _ref20 => {
          let {
            measureRef
          } = _ref20;
          return createRoot(measureRef);
        }
      });
    }
    return createRoot();
  }
}
_defineProperty(List, "defaultProps", {
  data: [],
  sortColumn: undefined,
  sortDirection: 'ASC',
  onSortChange: undefined,
  onSelectionChange: undefined,
  selection: [],
  renderSelectionCheckbox: undefined,
  rowKey: 'key',
  renderRowBody: undefined,
  expandedRows: [],
  notExpandableRows: [],
  onExpandedRowsChange: undefined,
  loadingRows: [],
  className: '',
  baseClassName: `${CLS_PREFIX}list`,
  pagination: undefined,
  totalRows: undefined,
  toolbar: undefined,
  allRowsSelected: false,
  onAllRowsSelectedChange: undefined,
  filtered: false,
  emptyView: undefined,
  emptyViewMode: 'all',
  sidebar: undefined,
  locale: undefined,
  vertical: VERTICAL_BREAKPOINT,
  loading: false,
  rowProps: undefined,
  reorderable: false,
  onReorderEnd: undefined,
  autoExpandSingleItem: true
});
export default List;